import { BenefitType, SupplementalPlanForEnrollment } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import {
  MonolithMethodNames,
  UseMonolithQueryProps,
  showErrorNotification,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { FormFooter } from '../../../../../FormFooter';
import {
  exportSupplementalPlanList,
  getDefaultSupplementalPlanFormFields,
  mapSupplementalPlanToPlanElectionItem,
} from '../../../../../ManualSubmissionFlow';
import {
  PlanElectionFormInputs,
  UsePlanElectionFormSubmitProps,
  mapPlanElectionItemToUpdateSubmittedPlanDto,
  usePlanElectionForm,
} from '../../../../../PlanElectionForm';

const dentalPlanMethod: MonolithMethodNames = 'dentalControllerSearch';

type Props = {
  onboardingPeriodId: string;
  employeeId: string;
  isOpen: boolean;
  onClose: () => void;
  benefitId?: string;
};

export const EmployeePagePlansBenefitCardDentalPlanElectionDrawer = ({
  onboardingPeriodId,
  employeeId,
  benefitId,
  isOpen,
  onClose,
}: Props) => {
  const idempotencyKey = useIdempotencyKey();

  const { tryMutate: mutateUpdateSubmitPlan } = useMonolithMutation({
    method: 'benefitsControllerUpdateSubmittedPlan',
  });
  const { data: benefits, isLoading } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const isEditMode = isDefined(benefitId);
  const dentalBenefit = benefits?.dentalBenefits?.find(
    ({ id }) => id === benefitId
  );

  const handleOnSubmit = async ({
    data,
  }: UsePlanElectionFormSubmitProps): Promise<boolean> => {
    if (isEditMode) {
      const result = await mutateUpdateSubmitPlan([
        benefitId,
        idempotencyKey.get(),
        mapPlanElectionItemToUpdateSubmittedPlanDto(data.plans),
      ]);

      return result.isOk();
    }
    showErrorNotification({
      message: 'Adding a new dental plan is not yet supported',
    });

    return false;
  };

  const {
    whoIsEnrolledInMultiplePlans,
    allWhoIsEnrolledOptions,
    insuredIdToNameMap,
    planElectionForm,
    insured,
    onSubmit,
  } = usePlanElectionForm({
    employeeId,
    onboardingPeriodId,
    onSubmit: handleOnSubmit,
    getDefaultFormFields: (allWhoIsEnrolled: string[] = []) => {
      return getDefaultSupplementalPlanFormFields(
        dentalBenefit ? [dentalBenefit] : [],
        allWhoIsEnrolled
      );
    },
  });

  if (!insured || isLoading) {
    return null;
  }

  const handleSubmitClick = async () => {
    if (await planElectionForm.trigger()) {
      const success = await onSubmit();
      if (success) {
        onClose();
      }
    }
  };

  const handleGetParams = (
    whoIsEnrolled: string[]
  ): UseMonolithQueryProps<typeof dentalPlanMethod>['params'] => {
    return [onboardingPeriodId, { insuredIds: whoIsEnrolled }];
  };

  const handleDownloadPlans = (
    dentalPlans: SupplementalPlanForEnrollment[] | undefined
  ) => {
    if (dentalPlans) {
      exportSupplementalPlanList(
        dentalPlans,
        `${insured.employee.firstName} ${insured.employee.lastName}`
      );
    }
  };

  return (
    <Drawer
      title={isEditMode ? 'Edit dental plan' : 'Add dental plan'}
      onClose={onClose}
      opened={isOpen}
      size="lg"
    >
      <PlanElectionFormInputs
        mapDataToPlanElectionItem={mapSupplementalPlanToPlanElectionItem}
        whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
        whoIsEnrolledOptions={allWhoIsEnrolledOptions}
        insuredIdToNameMap={insuredIdToNameMap}
        benefitType={BenefitType.SUPPLEMENTAL_DENTAL}
        source="plan_election_drawer"
        onDownloadPlans={handleDownloadPlans}
        planElectionForm={planElectionForm}
        method={dentalPlanMethod}
        getParams={handleGetParams}
        hasMoreThanOnePlan={false}
        index={0}
      />
      <FormFooter
        primaryLabel="Confirm and submit election"
        primaryButtonProps={{ onClick: handleSubmitClick }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onClose, mr: 'auto' }}
      />
    </Drawer>
  );
};
